import {
  action,
  computed,
  makeAutoObservable,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from "mobx";
import { IPromiseBasedObservable, fromPromise } from "mobx-utils";

import {
  Configuration,
  PermissionDto,
  UserDto,
  UserFilterDto,
  UserProfileApi,
  UserRegisterDto,
  UserRelationsEnum,
  WeeklyWorkDayDto,
  WorkPlanDto,
} from "../../api-client";
import AuthStore from "../Auth/authStore";
import notificationStore from "../Notification/notificationStore";
import { queryClient } from "../queryClient";
import { calendarKeys, invalidateCalendarAppointmentCaches } from "../Calendar/calendar.keys";

export default class UserStore {
  public authStore: AuthStore;
  //public user: UserDto|null = null;
  public user: UserDto | null = null;
  //public authStore: AuthStore;

  public allUsersBasic: UserDto[] = [];

  public permissionMap: Map<string, PermissionDto> = new Map<
    string,
    PermissionDto
  >();

  public apiClient: UserProfileApi;

  public apiInfo: any | null = null;

  public debugUI: boolean = false;

  //  @observable
  // public authToken: string;

  public constructor(apiClient: UserProfileApi, authStore: AuthStore) {
    makeAutoObservable(this, {
      user: observable,
      permissionMap: observable,
      apiInfo: observable,
      debugUI: observable,
      resourceRequest: observable,
      setUser: action,
      userId: computed,
      // Add computed and action annotations as needed
    });
    this.apiClient = apiClient;
    this.authStore = authStore;

    //this.authStore = authStore;
    //this.authToken = authToken;
  }

  // derived, stable scalar; great for selectors/queries
  get userId(): string | undefined {
    return this.user?.id ?? undefined;
  }

  
  public fetchAPIInfo = async () => {
    try {
      const { data } = (await this.apiClient.apiInfo()) as any;
      runInAction(() => {
        this.apiInfo = data;
      });
      return data;
    } catch (error: any) {
      console.error("Error fetching API info:", error);
      const errorMessage =
        error.response?.data?.message || "Error fetching API Data";
      notificationStore.showMessage(errorMessage, "error", error);
      throw error;
    }
  };

  @action
  public setUser(user: UserDto) {
    this.user = user;
    this.permissionMap.clear();

    if (user.permissions) {
      user.permissions.forEach((permission) => {
        this.permissionMap.set(permission.id, permission);
      });
    }
  }

  // @computed private get isRole() {
  // return (roleName: string) => {
  private isRole(roleName: string) {
    // console.log("Checking role:", this.user, roleName);
    //if(this.user?.roles) console.log("Roles:", this.user?.roles[0]);
    if (
      this.user &&
      this.user.roles &&
      this.user.roles.map((role: any) => role.name).includes(roleName)
    ) {
      return true;
    } else {
      return false;
    }
  }

  hasPermission = (permissionId: string) => {
    return this.user && this.permissionMap.has(permissionId) ? true : false;
  };

  // Use @action to mark methods that modify observable state
  isTherapist = () => {
    return this.isRole("Therapist");
  };

  getRoles = (): string[] => {
    if (this.user && this.user.roles) {
      const roles = this.user.roles as any[];
      return roles.map((role) => role.name);
    } else {
      return [];
    }
  };

  listPermissions = (): string[] => {
    if (this.user && this.user.permissions) {
      return this.user.permissions.map((permission) => permission.name);
    } else {
      return [];
    }
  };

  public resourceRequest: IPromiseBasedObservable<any> | null = null;

  public userRegister = async (userRegisterDto: UserRegisterDto) => {
    const { data } = (await this.apiClient.userRegister(
      userRegisterDto
    )) as any;
    return data || [];
  };

  public changeUserLoginDetails = async (
    userRegisterDto: UserRegisterDto,
    token: string | null
  ) => {
    const basePath = process.env.REACT_APP_API_URL;
    const config = new Configuration({
      basePath: basePath,
      // baseOptions: {
      //   headers: {
      //     Authorization: `Bearer ${token}`,
      //     "X-Custom-Header": "value",
      //   }
      // }
    });
    const apiClientWithCustomToken = new UserProfileApi(config);
    try {
      const { data } = (await apiClientWithCustomToken.changeUserLoginDetails(
        userRegisterDto,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Custom-Header": "value",
          },
        }
      )) as any;
      if (this.user) {
        this.setUser(data);
      }
      notificationStore.showMessage(
        "Benutzeranmeldeinformationen erfolgreich geändert",
        "success"
      );
      return data || [];
    } catch (error: any) {
      console.error("Error fetching user:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Benutzeranmeldeinformationen konnten nicht gespeichert werden.";

      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public sendOtp = async (user: string) => {
    try {
      // The response carries no secret any more, only that the mail went out.
      await this.apiClient.sendOtp(user);
      notificationStore.showMessage(
        "Email wurde erfolgreich gesendet",
        "success"
      );
      return true;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "OTP konnte nicht gesendet werden";

      notificationStore.showMessage(errorMessage, "error", error);
      return false;
    }
  };

  public changeUserLocation = async (locationId: string) => {
    try {
      // console.log("User:", this.user?.id, locationId);
      const { data } = (await this.apiClient.changeUserLocation(
        this.user?.id as string,
        locationId
      )) as any;
      // Set the notification message using the provided notification store
      if (data) {
        notificationStore.showMessage(
          "Benutzerstandort erfolgreich geändert",
          "success"
        );
      }

      if (this.user) {
        this.setUser(data);
      }

      /**
       * Invalidate any queries the implicitly depend on the user's location
       */
      invalidateCalendarAppointmentCaches(queryClient);

      return data || [];
    } catch (error: any) {
      console.error("Error fetching user:", error);
      const errorMessage =
        error.response?.data?.message || "Fehler beim Benutzerupdate";

      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  // DEPRECATED: Use useSaveUserInfo mutation from user.mutations.ts instead
  // public saveUserInfo = async (user: UserDto) => {
  //   try {
  //     const { data } = (await this.apiClient.saveUserInfo(user)) as any;

  //     notificationStore.showMessage("Benutzerdaten wurden erfolgreich gespeichert!", "success");

  //     return data.saved || [];
  //   } catch (error: any) {
  //     console.error("Error fetching user:", error);
  //     const errorMessage =
  //       error.response?.data?.message || "Fehler beim Speichern der Benutzerdaten";

  //     notificationStore.showMessage(errorMessage, "error", error);
  //     return null;
  //   }
  // };

  public sendActivationEmail = async (
    id: string
  ): Promise<{ link: string }> => {
    try {
      const response =
        await this.apiClient.userProfileControllerGenerateActivationLink({
          data: { id },
        });
      notificationStore.showMessage(
        "Aktivierungs-E-Mail wurde erfolgreich gesendet",
        "success"
      );
      return response.data as unknown as { link: string };
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Fehler beim Senden der Aktivierungs-E-Mail";
      notificationStore.showMessage(errorMessage, "error", error);
      throw error;
    }
  };

  /**
   * This gets the autenticated user and their role and permission information
   * 
   * @returns 
   */
  public getUser = async (): Promise<UserDto | null> => {
    try {
      const relations = [
        // UserRelationsEnum.Location,
        // UserRelationsEnum.TherapistLocation,
        // UserRelationsEnum.Therapies,
        UserRelationsEnum.Roles,
        UserRelationsEnum.RolesPermissions,
        UserRelationsEnum.Permissions,
        UserRelationsEnum.Business,
        // UserRelationsEnum.WeeklyWorkDays,
        // UserRelationsEnum.LocationAddress,
      ];
      const response = (await this.apiClient.getAuthenticatedUser(
        relations
      )) as any;
      // console.log("Response:", response);
      this.setUser(response.data);
      //console.log("User:", this.user);

      this.fetchAPIInfo();
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        // Handle 401 error
        console.error("Unauthorized access. logging out");
        this.authStore.setAuthToken(null);
      } else {
        // Handle other errors
        console.error("An error occurred:", error.message);
      }
    }
    return null;
  };

  public activateOrDeactivateUser = async (
    userId: string,
    softDeleteUser: boolean
  ) => {
    try {
      const { data } = (await this.apiClient.activateOrDeactivateUser(
        userId,
        softDeleteUser
      )) as any;

      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
      throw error;
    }
  };


  // TODO replace with a react-query solution
  public getAllUsersBasic = async (
    filters: UserFilterDto
  ): Promise<UserDto[]> => {
    const { limit, page, relations, withDeleted } = filters;
    const { data } = await this.apiClient.getAllUsersBasic(
      limit,
      page,
      relations,
      withDeleted
    );
    this.allUsersBasic = data;
    return data || [];
  };

  public getUsersBy = async (locationId: string): Promise<UserDto[]> => {
    try {
      const { data } = (await this.apiClient.getUsersBy(locationId)) as any;
      return data || [];
    } catch (error: any) {
      console.error("Error fetching user:", error);
      const errorMessage =
        error.response?.data?.message || "internal server error";
      notificationStore.showMessage(errorMessage, "error", error);
      throw error; // or return [] if you want to return an empty array
    }
  };


  /**
   * This can be used as a final block to ensure that the user has the correct permissions and will throw an error
   * You should do a check upstream of this to avoid throwing errors
   *
   * @param criteria
   */
  enforce(criteria: boolean) {
    if (!criteria) throw new Error("Permissions enformcement failed");
  }

  public verifyShortToken = async (token: string) => {
    const basePath = process.env.REACT_APP_API_URL;
    const config = new Configuration({
      basePath: basePath,
      baseOptions: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    const apiClientWithToken = new UserProfileApi(config);

    try {
      const { data } =
        (await apiClientWithToken.userProfileControllerVerifyShortToken({
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })) as any;
      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Token konnte nicht verifiziert werden";
      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public hardDeletePreflight = async (
    userId: string
  ): Promise<{ canDelete: boolean; reasons: string[]; details: any }> => {
    const basePath = process.env.REACT_APP_API_URL;
    try {
      const response = await fetch(
        `${basePath}/user-profile/hardDelete/preflight/${userId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.authStore.authToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        let message = "Preflight-Check fehlgeschlagen";
        try {
          const errorData = await response.json();
          message = errorData?.message || message;
        } catch {}
        const err: any = new Error(message);
        err.status = response.status;
        throw err;
      }
      return await response.json();
    } catch (error: any) {
      console.error("Preflight-Check Fehler:", error);
      notificationStore.showMessage(
        error.message || "Preflight-Check fehlgeschlagen",
        "error",
        error
      );
      throw error;
    }
  };

  public hardDeleteUser = async (userId: string) => {
    try {
      // Erstelle eine Anfrage mit dem aktuellen Authorization-Header
      const basePath = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${basePath}/user-profile/hardDelete/${userId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${this.authStore.authToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        let message = "Fehler beim endgültigen Löschen des Benutzers";
        try {
          const errorData = await response.json();
          message = errorData?.message || message;
        } catch {}
        const err: any = new Error(message);
        err.status = response.status;
        throw err;
      }

      notificationStore.showMessage(
        "Benutzer wurde endgültig gelöscht",
        "success"
      );
      return true;
    } catch (error: any) {
      console.error("Fehler beim Hard-Delete des Benutzers:", error);
      const errorMessage =
        error.message || "Fehler beim endgültigen Löschen des Benutzers";
      notificationStore.showMessage(errorMessage, "error", error);
      throw error;
    }
  };

  @action
  public sendUserActivationEmail = async (userId: string) => {
    try {
      const { data } = (await this.apiClient.sendUserActivationEmail(userId)) as any;
      notificationStore.showMessage("Aktivierungsmail wurde erfolgreich gesendet", "success");
      return data || [];
    } catch (error: any) {
      console.error("Error sending activation email:", error);
      const errorMessage = error.response?.data?.message || "Aktivierungsmail konnte nicht gesendet werden";
      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
}

setDebugUI = (enabled: boolean) => {
    this.debugUI = enabled;
  };
}
